/**
 * Quick-add — "exhibition mode", and the most important interaction in the app.
 *
 * The scenario it is built for: a sales rep at a congress, phone in one hand,
 * a doctor in front of them, thirty seconds before the next conversation. What
 * they actually have is a surname, a number, and something worth remembering.
 *
 * Every design decision here follows from that:
 *
 *  - **Six fields, and only one is required.** Everything else is editable
 *    later on the profile; a form that demands a first name at a congress is a
 *    form that gets abandoned.
 *  - **The note becomes the first interaction.** «کجا آشنا شدید» is the single
 *    most valuable thing captured at a stand, and asking someone to open a
 *    second screen to record it means it never gets recorded.
 *  - **"Save and add another" keeps the dialog open** and returns focus to the
 *    surname. A queue of people at a stand is the normal case, not the
 *    exception, and closing the dialog between each one is pure friction.
 *  - **Channel checkboxes, not a channel picker.** One number is usually on
 *    WhatsApp *and* Eitaa; two taps beats a multi-select.
 *
 * Two round trips, not one: createContact then recordInteraction. A combined
 * mutation would put a "create a contact and maybe an interaction" special case
 * into the domain, and the second call is a few milliseconds on a form that
 * takes twenty seconds to fill.
 */
import { Button } from '@meshhub/design-tokens/ui';
import { ChevronDown, ChevronUp, Send } from 'lucide-react';
import React, { useState } from 'react';
import { S } from '../lib/strings.js';
import { useQuickAdd } from '../lib/useQuickAdd.js';
import { ContactCrmFields, LookupField } from './contact/ContactCrmFields.web.jsx';
import { SendMessageDialog } from './messaging/SendMessageDialog.web.jsx';
import { CheckboxField, FormDialog, MutationError, SelectField, TextArea, TextField }
  from './FormKit.web.jsx';

/**
 * The DESKTOP variant. Its logic lives in `useQuickAdd` because `.mobile.jsx`
 * shares it — a reduction, never a fork (CLAUDE.md §12). The natural way to
 * write the mobile screen is to copy this one and delete things, and three
 * months later the two build different payloads.
 */
export function QuickAddDialog({ open, onOpenChange, onCreated }) {
  const {
    form,
    set,
    toggleChannel,
    submit,
    error,
    justAdded,
    creating,
    channels,
    facilityTypes,
    defaultType,
    lastNameRef,
  } = useQuickAdd({ open, onCreated, onDone: () => onOpenChange(false) });

  // CLOSED by default, and that is the whole point. Exhibition mode is a
  // surname, a number and thirty seconds; everything behind this disclosure is
  // for somebody filling a profile in at a desk. A reduction may hide a field
  // behind a disclosure — it may not drop one (CLAUDE.md §12), which is why
  // the mobile sheet carries the same section rather than a shorter list.
  const [showCrm, setShowCrm] = useState(false);
  const [messaging, setMessaging] = useState(null);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.quickAdd.title}
      hint={S.quickAdd.hint}
      onSubmit={() => submit({ keepOpen: false })}
      submitLabel={S.quickAdd.submit}
      busy={creating}
      footer={
        <Button type="button" onClick={() => submit({ keepOpen: true })} disabled={creating}>
          {S.quickAdd.submitAndAnother}
        </Button>
      }
    >
      <MutationError error={error} />
      {justAdded && (
        <p
          role="status"
          className="flex flex-wrap items-center gap-3 rounded border border-success px-4 py-2
                     text-base text-success"
        >
          {S.quickAdd.added(justAdded.displayName)}
          {/* «ارسال پیام» from the create form. The dialog is the messaging
              service's own, so the template list, the server-rendered preview
              and the suppression reason are the ones the Outbox would show —
              a second send path with its own opinion is exactly what the
              messaging rules cannot survive. */}
          <Button type="button" variant="ghost" onClick={() => setMessaging(justAdded)}>
            <Send size={13} aria-hidden="true" />
            {S.quickAdd.sendMessage}
          </Button>
        </p>
      )}

      <div className="grid grid-cols-2 gap-4">
        <TextField
          ref={lastNameRef}
          label={S.quickAdd.lastName}
          value={form.lastName}
          onChange={(e) => set({ lastName: e.target.value })}
          autoFocus
          required
        />
        <TextField
          label={S.quickAdd.firstName}
          value={form.firstName}
          onChange={(e) => set({ firstName: e.target.value })}
        />
      </div>

      <TextField
        label={S.quickAdd.phone}
        value={form.phone}
        onChange={(e) => set({ phone: e.target.value })}
        dir="ltr"
        inputMode="tel"
        // Persian digits are accepted and converted on submit, so the rep can
        // type on whichever keyboard is already open.
        placeholder="09121234567"
      />

      <fieldset className="flex flex-wrap gap-4">
        <legend className="mb-2 text-sm text-text-muted">
          {S.contacts.detail.channels}
        </legend>
        {channels.map((channel) => (
          <CheckboxField
            key={channel.id}
            label={channel.labelFa}
            checked={form.channels.includes(channel.code)}
            onChange={() => toggleChannel(channel.code)}
          />
        ))}
      </fieldset>

      {/* BOTH are `LookupField` — the same component the CRM section below
          uses — rather than plain selects over the same query.

          Two things that were wrong with a plain select: an empty vocabulary
          rendered as a dropdown holding nothing and SAYING nothing, which
          looks exactly like a list that failed to load; and there was no way
          to add «گویابات» from the form where you had just noticed it was
          missing, so the rep had to abandon a half-typed contact and go and
          find /settings/lookups. `VocabularyField` underneath says «هنوز
          گزینه‌ای تعریف نشده است», opens its own creator when the list is
          empty, and offers «گزینه تازه» to an ADMIN at any time — while a USER
          gets a sentence naming what to ask for, because creating a lookup is
          ADMIN at the BFF and again at the service. */}
      <LookupField
        kind="FIELD_OF_WORK"
        label={S.quickAdd.fieldOfWork}
        value={form.fieldOfWorkId}
        onChange={(id) => set({ fieldOfWorkId: id })}
      />

      <LookupField
        kind="PRODUCT"
        label={S.quickAdd.product}
        value={form.productId}
        onChange={(id) => set({ productId: id })}
      />

      <TextArea
        label={S.quickAdd.note}
        placeholder={S.quickAdd.notePlaceholder}
        hint={defaultType ? S.quickAdd.noteBecomesInteraction : undefined}
        rows={3}
        value={form.note}
        onChange={(e) => set({ note: e.target.value })}
      />

      <Button
        type="button"
        variant="ghost"
        className="self-start"
        aria-expanded={showCrm}
        onClick={() => setShowCrm((v) => !v)}
      >
        {showCrm ? (
          <ChevronUp size={14} aria-hidden="true" />
        ) : (
          <ChevronDown size={14} aria-hidden="true" />
        )}
        {S.quickAdd.crmDetails}
      </Button>

      {showCrm && (
        <>
          <p className="text-xs text-text-muted">{S.quickAdd.crmDetailsHint}</p>
          <ContactCrmFields
            values={form}
            onChange={set}
            facilityTypes={facilityTypes}
          />
        </>
      )}

      {/* Mounted only when it is wanted. Rendered closed it would still run
          its template query on every quick-add, which is a round trip for a
          dialog nobody has asked for. */}
      {messaging && (
        <SendMessageDialog
          open
          onOpenChange={(next) => !next && setMessaging(null)}
          contact={messaging}
        />
      )}
    </FormDialog>
  );
}
